import { type ApolloCache } from '@apollo/client';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';

import * as graphql from '@treams/treams-network';

import { placementPosition } from '../talentReviewCellOrder';
import {
	type Rollback,
	type Save,
	applyServerPlacement,
	keepLatestMotivation,
	keepSentRetentionRisk,
	mergePlacement,
	nextCachedGrid,
	nextPlacementHelpGrid,
	persistKind,
	releaseRollback,
	settleRollback,
	takeRollback,
} from '../talentReviewGridPersistence';

export type TalentReviewGridIdentity = {
	templateID: string;
	groupID: string;
	localDate: graphql.Scalars['LocalDate']['input'];
};

type Placement = graphql.TalentReviewGridPlacement;

/**
 * Owns the optimistic placement state of a talent review grid and persists
 * changes through the update/remove placement mutations, including rollback on
 * failure and keeping the Apollo query cache in sync.
 *
 * While a different grid is loading (after switching template, group or date)
 * `grid` can still be the previously loaded one. Such a grid is `stale`:
 * persisting is blocked until the loaded grid matches `expected` again.
 * In `local` mode (a superuser correcting a submitted snapshot) nothing is
 * persisted: changes only update the optimistic state and are saved in one go
 * by the caller. `reset` puts the state back to the loaded grid.
 * @param options hook input
 * @param options.grid the currently loaded grid
 * @param options.expected the grid identity currently selected in the UI
 * @param options.local keep changes in memory instead of persisting them
 */
export default function useTalentReviewGridPersistence(options: {
	grid: graphql.TalentReviewGrid;
	/** The grid identity currently selected in the UI. */
	expected: TalentReviewGridIdentity;
	local?: boolean;
}) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { grid, expected, local } = options;

	const stale =
		grid.template.id !== expected.templateID ||
		grid.group.id !== expected.groupID ||
		grid.localDate !== expected.localDate;

	const [placements, setPlacements] = useState(grid.placements);
	const pendingSaves = useRef(0);

	// Saves are numbered in the order they start. Responses can arrive out of
	// order (a slow earlier save finishing after a faster later one) and each
	// response carries the whole grid, while a save only ever changes one
	// user's placement. A response can therefore predate a concurrent save for
	// another user, so it is only trusted for its own user: applied to the
	// cache while no newer response for that user was written, and to the
	// optimistic state while no newer save for that user has started.
	const saveSequence = useRef(0);
	const startedSaves = useRef(new Map<string, number>());
	const appliedSaves = useRef(new Map<string, number>());
	// Highest sequence whose activity list has been written to the cache. The
	// activity is grid-wide and complete in every response, so the newest
	// response wins and an out-of-order older one cannot roll the list back.
	const appliedActivity = useRef(0);

	const startSave = (userID: string, identity: TalentReviewGridIdentity): Save => {
		pendingSaves.current += 1;
		saveSequence.current += 1;
		startedSaves.current.set(userID, saveSequence.current);
		return { sequence: saveSequence.current, userID, identity };
	};

	// Rollback state per user: the placement to restore when a save for that
	// user fails. Starts as the placement before the user's first in-flight
	// save; a successful save moves it to the server's value so a failure of an
	// older overlapping save cannot revert past that success. Saves for other
	// users are unaffected by a rollback.
	const rollbacks = useRef(new Map<string, Rollback<Placement>>());

	const [updatePlacement] = graphql.useUpdateTalentReviewGridPlacementMutation();
	const [removePlacement] = graphql.useRemoveTalentReviewGridPlacementMutation();
	const [clearGridMutation] = graphql.useClearTalentReviewGridMutation();
	const [answerPlacementHelpMutation] = graphql.useAnswerTalentReviewPlacementHelpMutation();

	// The mutation response contains the full grid, but TalentReviewGrid has no
	// id for Apollo to normalize on. Write the response back to the query cache
	// ourselves, otherwise the old position stays in the cache after switching
	// dates. Only this save's own user is taken from the response; the other
	// placements stay as cached, so an out-of-order response cannot undo a
	// concurrent save for somebody else.
	const writeGridQuery = (
		cache: ApolloCache<unknown>,
		identity: TalentReviewGridIdentity,
		localDate: string,
		next: graphql.TalentReviewGridQuery['talentReviewGrid'],
	) => {
		cache.writeQuery<graphql.TalentReviewGridQuery, graphql.TalentReviewGridQueryVariables>({
			query: graphql.TalentReviewGridDocument,
			variables: {
				templateID: identity.templateID,
				groupID: identity.groupID,
				localDate,
			},
			data: { talentReviewGrid: next },
		});
	};

	const readGridQuery = (
		cache: ApolloCache<unknown>,
		identity: TalentReviewGridIdentity,
		localDate: string,
	) =>
		cache.readQuery<graphql.TalentReviewGridQuery, graphql.TalentReviewGridQueryVariables>({
			query: graphql.TalentReviewGridDocument,
			variables: {
				templateID: identity.templateID,
				groupID: identity.groupID,
				localDate,
			},
		})?.talentReviewGrid;

	const writeGridToCache = (
		save: Save,
		cache: ApolloCache<unknown>,
		next: graphql.TalentReviewGridQuery['talentReviewGrid'] | undefined,
	) => {
		if (!next) {
			return;
		}

		const cached = readGridQuery(cache, save.identity, next.localDate);
		const written = nextCachedGrid(save, appliedSaves.current, appliedActivity, cached, next);
		if (!written) {
			return;
		}

		writeGridQuery(cache, save.identity, next.localDate, written);
	};

	// Fresh query data (e.g. after navigating back with cache-and-network) must
	// refresh the local state; just not while a save is still in flight,
	// otherwise stale data overwrites the optimistic position. Applied during
	// render rather than in an effect, so the placements change in the same
	// render as the grid's day: the sidebar freezes its per-day order on that
	// switch and must not capture the previous day's placements.
	const [syncedPlacements, setSyncedPlacements] = useState(grid.placements);
	if (syncedPlacements !== grid.placements) {
		setSyncedPlacements(grid.placements);
		if (pendingSaves.current === 0) {
			setPlacements(grid.placements);
		}
	}

	// The server can deviate from the optimistic state: a remove on a day with
	// its own writes falls back to the last known earlier placement. Apply the
	// mutation response directly — the sync effect above skips it because
	// pendingSaves is still > 0 at that point. Only this save's own user, and
	// only while no newer save for that user has started; otherwise the newer
	// optimistic state wins.
	const applyServerGrid = (
		save: Save,
		next: graphql.TalentReviewGridQuery['talentReviewGrid'] | undefined,
	) => {
		const result = applyServerPlacement(
			save,
			startedSaves.current,
			next as { placements: readonly Placement[] } | undefined,
		);
		if (!result.apply) {
			return;
		}

		setPlacements((current) => mergePlacement(current, result.placement, save.userID));
	};

	const reset = () => setPlacements(grid.placements);

	const persist = async (
		next: graphql.TalentReviewGridPlacement[],
		userID: string,
		remove?: boolean,
	) => {
		const kind = persistKind({
			local,
			stale,
			remove,
			hasPlacement: next.some((item) => item.user.id === userID),
		});

		if (kind === 'local') {
			setPlacements(next);
			return;
		}

		if (kind === 'skip' || kind === 'missing') {
			return;
		}

		// A pending debounced motivation save for this user is superseded: an
		// update already carries the latest motivation in `next`, and after a
		// remove the motivation is gone with the placement. Letting it fire
		// would replay the old row/column (or re-add a removed person).
		debouncedSaves.current.get(userID)?.cancel();

		const identity = {
			templateID: grid.template.id,
			groupID: grid.group.id,
			localDate: grid.localDate,
		};

		const rollback = takeRollback(
			rollbacks.current,
			userID,
			placements.find((item) => item.user.id === userID),
		);

		setPlacements(next);
		const save = startSave(userID, identity);

		try {
			if (kind === 'remove') {
				const result = await removePlacement({
					variables: {
						input: {
							templateID: identity.templateID,
							groupID: identity.groupID,
							userID,
							localDate: identity.localDate,
						},
					},
					update: (cache, { data }) =>
						writeGridToCache(save, cache, data?.removeTalentReviewGridPlacement.grid),
				});
				const serverGrid = result.data?.removeTalentReviewGridPlacement.grid;
				settleRollback(rollback, serverGrid?.placements as Placement[] | undefined, userID);
				applyServerGrid(save, serverGrid);
				return;
			}

			const placement = next.find((item) => item.user.id === userID);
			if (!placement) {
				return;
			}

			const savedGrid = (grid: graphql.TalentReviewGridQuery['talentReviewGrid'] | undefined) =>
				keepSentRetentionRisk(grid, userID, placement.retentionRisk);

			const result = await updatePlacement({
				variables: {
					input: {
						templateID: identity.templateID,
						groupID: identity.groupID,
						userID,
						localDate: identity.localDate,
						row: placement.row,
						column: placement.column,
						// Every write carries the place in the cell, so an edit (a
						// motivation, a score) does not move the person to the end.
						position: placementPosition(placement),
						motivation: placement.motivation,
						score: placement.score,
						// The placement is the full current state, so null here is
						// a clear (the API clears on null, keeps on omitted).
						retentionRisk: placement.retentionRisk,
					},
				},
				update: (cache, { data }) =>
					writeGridToCache(save, cache, savedGrid(data?.updateTalentReviewGridPlacement.grid)),
			});
			const serverGrid = savedGrid(result.data?.updateTalentReviewGridPlacement.grid);
			settleRollback(rollback, serverGrid?.placements as Placement[] | undefined, userID);
			applyServerGrid(save, serverGrid);
		} catch {
			// Only this user's placement rolls back; concurrent saves for other
			// users keep their optimistic state.
			setPlacements((current) => mergePlacement(current, rollback.placement, userID));
			addToast(t('talentReviews.v2.saveFailed'), {
				appearance: 'error',
			});
		} finally {
			pendingSaves.current -= 1;
			releaseRollback(rollbacks.current, userID, rollback);
		}
	};

	// The motivation typed most recently, per user. A save's response carries
	// the text as it was when the debounced save fired; anything typed after
	// that is newer and must not be overwritten by the response.
	const latestMotivations = useRef(new Map<string, string | null | undefined>());

	// Saves one placement's motivation. Unlike `persist` this takes the grid
	// identity as an argument, captured when the keystroke happened: a debounce
	// can flush after a date switch has already made the mounted grid stale (or
	// unmounted the hook), and the save must still target the grid that was
	// being edited. No rollback on failure — the typed text stays, the toast
	// reports the miss.
	const saveMotivation = async (
		placement: graphql.TalentReviewGridPlacement,
		identity: TalentReviewGridIdentity,
	) => {
		const userID = placement.user.id;
		const save = startSave(userID, identity);
		const sentRetentionRisk = placement.retentionRisk || undefined;
		const withLatest = (next: graphql.TalentReviewGridQuery['talentReviewGrid'] | undefined) =>
			keepSentRetentionRisk(
				keepLatestMotivation(
					next,
					userID,
					placement.motivation,
					latestMotivations.current.get(userID),
				),
				userID,
				sentRetentionRisk,
			);

		try {
			const result = await updatePlacement({
				variables: {
					input: {
						templateID: identity.templateID,
						groupID: identity.groupID,
						userID: placement.user.id,
						localDate: identity.localDate,
						row: placement.row,
						column: placement.column,
						position: placementPosition(placement),
						motivation: placement.motivation,
						score: placement.score,
						// A debounced motivation save must not clear a rating chosen
						// after it was scheduled. Omitting null keeps the stored one.
						retentionRisk: placement.retentionRisk || undefined,
					},
				},
				update: (cache, { data }) =>
					writeGridToCache(save, cache, withLatest(data?.updateTalentReviewGridPlacement.grid)),
			});
			applyServerGrid(save, withLatest(result.data?.updateTalentReviewGridPlacement.grid));
		} catch {
			addToast(t('talentReviews.v2.saveFailed'), {
				appearance: 'error',
			});
		} finally {
			pendingSaves.current -= 1;
		}
	};

	const saveMotivationRef = useRef(saveMotivation);
	saveMotivationRef.current = saveMotivation;

	// Typing a motivation calls this per keystroke: the optimistic state
	// updates immediately, but the mutation is debounced per user so a burst of
	// keystrokes results in one save instead of one per character.
	const debouncedSaves = useRef(
		new Map<
			string,
			_.DebouncedFunc<
				(placement: graphql.TalentReviewGridPlacement, identity: TalentReviewGridIdentity) => void
			>
		>(),
	);

	const persistMotivation = (next: graphql.TalentReviewGridPlacement[], userID: string) => {
		setPlacements(next);

		if (local) {
			return;
		}

		const placement = next.find((item) => item.user.id === userID);
		if (!placement) {
			return;
		}

		latestMotivations.current.set(userID, placement.motivation);

		let save = debouncedSaves.current.get(userID);
		if (!save) {
			save = _.debounce(
				(latest: graphql.TalentReviewGridPlacement, identity: TalentReviewGridIdentity) => {
					void saveMotivationRef.current(latest, identity);
				},
				600,
				{ maxWait: 3000 },
			);
			debouncedSaves.current.set(userID, save);
		}
		save(placement, {
			templateID: grid.template.id,
			groupID: grid.group.id,
			localDate: grid.localDate,
		});
	};

	/**
	 * Saves a pending debounced motivation for one user right away, e.g. when
	 * the reviewer moves on to the next person. No-op when nothing is pending.
	 * @param userID whose motivation to flush
	 */
	const flushMotivation = (userID: string) => {
		debouncedSaves.current.get(userID)?.flush();
	};

	// Save pending motivations before the hook unmounts (e.g. switching dates
	// remounts the page component), otherwise the last keystrokes are lost.
	useEffect(() => {
		const saves = debouncedSaves.current;
		return () => saves.forEach((save) => save.flush());
	}, []);

	// Empties the grid in one mutation (one activity line) instead of a remove
	// per person. Optimistic: the grid empties right away and comes back as it
	// was when the server refuses. A clear touches every placed user, so it
	// claims one save sequence for all of them: responses of older per-user
	// saves that arrive later are ignored, and the whole response grid is
	// written to the cache.
	const clearGrid = async () => {
		if (local) {
			setPlacements([]);
			return;
		}

		if (stale) {
			return;
		}

		for (const save of debouncedSaves.current.values()) {
			save.cancel();
		}

		const identity = {
			templateID: grid.template.id,
			groupID: grid.group.id,
			localDate: grid.localDate,
		};
		const before = placements;

		pendingSaves.current += 1;
		saveSequence.current += 1;
		const sequence = saveSequence.current;
		for (const placement of before) {
			startedSaves.current.set(placement.user.id, sequence);
		}
		setPlacements([]);

		try {
			const result = await clearGridMutation({
				variables: { input: identity },
				update: (cache, { data }) => {
					const next = data?.clearTalentReviewGrid.grid;
					if (!next) {
						return;
					}
					for (const placement of before) {
						appliedSaves.current.set(placement.user.id, sequence);
					}
					appliedActivity.current = Math.max(appliedActivity.current, sequence);
					writeGridQuery(cache, identity, next.localDate, next);
				},
			});

			// Only trust the response while no newer save has started since; a
			// placement made right after the clear must keep its optimistic state.
			const next = result.data?.clearTalentReviewGrid.grid;
			const superseded = [...startedSaves.current.values()].some((started) => started > sequence);
			if (next && !superseded) {
				setPlacements(next.placements as graphql.TalentReviewGridPlacement[]);
			}
		} catch {
			setPlacements(before);
			addToast(t('talentReviews.v2.saveFailed'), {
				appearance: 'error',
			});
		} finally {
			pendingSaves.current -= 1;
		}
	};

	// Stores a completed placement-help questionnaire as an activity. Nothing
	// changes on the grid itself; the response only refreshes the activity
	// list, so the cached placements are kept and the rest is taken from the
	// response.
	const answerPlacementHelp = async (
		userID: string,
		answers: graphql.TalentReviewPlacementHelpAnswerInput[],
	) => {
		if (local || stale) {
			return;
		}

		const identity = {
			templateID: grid.template.id,
			groupID: grid.group.id,
			localDate: grid.localDate,
		};
		saveSequence.current += 1;
		const sequence = saveSequence.current;

		try {
			await answerPlacementHelpMutation({
				variables: { input: { ...identity, userID, answers } },
				update: (cache, { data }) => {
					const next = data?.answerTalentReviewPlacementHelp.grid;
					if (!next) {
						return;
					}
					const cached = readGridQuery(cache, identity, next.localDate);
					const written = nextPlacementHelpGrid(sequence, appliedActivity, cached, next);
					if (!written) {
						return;
					}
					writeGridQuery(cache, identity, next.localDate, written);
				},
			});
		} catch {
			addToast(t('talentReviews.v2.saveFailed'), {
				appearance: 'error',
			});
		}
	};

	return {
		placements,
		stale,
		persist,
		persistMotivation,
		flushMotivation,
		reset,
		clearGrid,
		answerPlacementHelp,
	};
}
